import React, { useState } from 'react';
import {
  Stack,
  Grid,
  TextField,
  Typography,
  Button,
  styled,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  DatePicker,
  TimePicker,
  LocalizationProvider,
  DesktopTimePicker,
} from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import dayjs from 'dayjs';
import CustomLocationAutocomplete from './LocationPicker';
import { AstroDetailsFormStyles, StyledButton } from './StyledComponents';

const BirthDetailsForm = ({ setFormData, formData, handleSubmit, name, setName }) => {
  const [errors, setErrors] = useState({});

  const setDob = (newValue) => {
    setFormData((prev) => ({
      ...prev,
      dob: newValue ? dayjs(newValue).format('DD/MM/YYYY') : null,
    }));
    setErrors((prev) => ({ ...prev, dob: null }));
  };

  const setTime = (newTime) => {
    setFormData((prev) => ({
      ...prev,
      timeOfBirth: newTime ? dayjs(newTime).format('HH:mm') : null,
    }));
    setErrors((prev) => ({ ...prev, timeOfBirth: null }));
  };

  const setPlace = (newPlace) => {
    setFormData((prev) => ({
      ...prev,
      placeOfBirth: newPlace,
    }));
    setErrors((prev) => ({ ...prev, placeOfBirth: null }));
  };

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const validateAndSubmit = (e) => {
    const newErrors = {};
    if (!formData.dob) newErrors.dob = 'Date of Birth is required';
    if (!formData.timeOfBirth) newErrors.timeOfBirth = 'Time of Birth is required';
    if (!formData.placeOfBirth) newErrors.placeOfBirth = 'Place of Birth is required';
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    handleSubmit(e);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Stack direction={'column'} spacing={8} sx={{ p: 2, width: '100%' }}>
        {/* Name */}
        <StyledTextField
          fullWidth
          variant="outlined"
          label="Name"
          value={name}
          onChange={(e) => setName && setName(e.target.value)}
          placeholder="What should we call you..."
        />

        <Stack
          direction={isMobile ? 'column' : 'row'}
          spacing={5}
          sx={{ width: '100%', justifyContent: 'flex-start' }}
        >
          {/* Date of Birth */}
          <StyledDatePicker
            label="Date of Birth"
            value={formData.dob ? dayjs(formData.dob, 'DD/MM/YYYY') : null}
            disableFuture
            onChange={setDob}
            format="DD/MM/YYYY"
            slotProps={{
              textField: {
                placeholder: 'DD/MM/YYYY',
                error: !!errors.dob,
                helperText: errors.dob,
              },
            }}
          />
          <Stack direction={'column'} spacing={1} sx={{ flex: 1 }}>
            <StyledTimePicker
              label="Time of Birth"
              value={
                formData.timeOfBirth
                  ? dayjs(formData.timeOfBirth, 'HH:mm')
                  : null
              }
              minutesStep={1}
              shouldDisableTime={(value, view) => {
                if (view === 'minutes') {
                  return false;
                }
                return false;
              }}
              views={['hours', 'minutes']}
              onChange={setTime}
              ampm
              slotProps={{
                textField: {
                  placeholder: '12:00 AM',
                  error: !!errors.timeOfBirth,
                  helperText: errors.timeOfBirth,
                },
                actionBar: {
                  sx: {
                    '& .MuiButton-root': {
                      color: theme.palette.getContrastText(
                        theme.palette.primary.main
                      ),
                      backgroundColor: 'transparent',
                      borderRadius: 1,
                      textTransform: 'none',
                    },
                  },
                },
              }}
            />
            <Typography variant="caption" sx={{ fontStyle: 'italic', mt: 1 }}>
              *Accurate <strong>Time</strong> help us for better predictions
            </Typography>
          </Stack>
        </Stack>

        {/* Place of Birth */}
        <CustomLocationAutocomplete
          onSelect={(place) => {
            setPlace(`${place.lat},${place.lng}`);
          }}
        />
        {errors.placeOfBirth && (
          <Typography color="error" variant="caption" sx={{ mt: -6 }}>
            {errors.placeOfBirth}
          </Typography>
        )}
        <Typography
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            paddingRight: '84px',
          }}
        >
          <StyledButton variant="outlined" onClick={validateAndSubmit}>
            <ArrowForwardIcon />
          </StyledButton>
        </Typography>
      </Stack>
    </LocalizationProvider>
  );
};

const StyledTextField = styled(TextField)(AstroDetailsFormStyles);
const StyledDatePicker = styled(DatePicker)(AstroDetailsFormStyles);
const StyledTimePicker = styled(DesktopTimePicker)(AstroDetailsFormStyles);

export default BirthDetailsForm;
